import { useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import Seo from "@/components/Seo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatError = (detail) => {
    if (detail == null) return "Login failed. Try again.";
    if (typeof detail === "string") return detail;
    if (Array.isArray(detail)) return detail.map((e) => e?.msg || JSON.stringify(e)).join(" ");
    return String(detail);
};

export default function AdminLogin() {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [error, setError] = useState("");
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError("");
        try {
            await axios.post(
                `${API}/auth/login`,
                { email, password },
                { withCredentials: true },
            );
            navigate("/admin");
        } catch (err) {
            setError(formatError(err.response?.data?.detail));
        } finally {
            setLoading(false);
        }
    };

    return (
        <main className="flex min-h-screen items-center justify-center px-6" data-testid="admin-login-page">
            <Seo title="Keeper Login — Logan's Reptiles & Exotics" siteName="Logan's Reptiles & Exotics" />
            <motion.form
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7 }}
                onSubmit={submit}
                className="w-full max-w-md border border-red-900/50 bg-coal p-10"
            >
                <div className="flex h-12 w-12 items-center justify-center border border-red-900/60 bg-red-950/30">
                    <Lock className="h-5 w-5 text-red-500" />
                </div>
                <h1 className="mt-6 font-display text-2xl font-extrabold text-bone">Keeper's Entrance</h1>
                <p className="mt-2 font-mono2 text-[10px] uppercase tracking-[0.25em] text-ash">
                    Inventory &amp; quote management
                </p>

                <label className="mt-8 block font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash">Email</label>
                <input
                    data-testid="admin-email-input"
                    type="email"
                    required
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="mt-2 w-full border border-red-950/60 bg-black/50 px-4 py-3 text-sm text-bone outline-none transition-colors duration-300 focus:border-red-700"
                />
                <label className="mt-5 block font-mono2 text-[10px] uppercase tracking-[0.2em] text-ash">Password</label>
                <input
                    data-testid="admin-password-input"
                    type="password"
                    required
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="mt-2 w-full border border-red-950/60 bg-black/50 px-4 py-3 text-sm text-bone outline-none transition-colors duration-300 focus:border-red-700"
                />

                {error && (
                    <p data-testid="admin-login-error" className="mt-4 border border-red-800/60 bg-red-950/30 p-3 text-xs text-red-400">
                        {error}
                    </p>
                )}

                <button
                    data-testid="admin-login-submit"
                    type="submit"
                    disabled={loading}
                    className="mt-7 w-full bg-crimson py-4 font-mono2 text-xs uppercase tracking-[0.25em] text-bone transition-colors duration-300 hover:bg-red-700 disabled:opacity-50"
                >
                    {loading ? "Unlocking…" : "Enter the Facility"}
                </button>
            </motion.form>
        </main>
    );
}
